/**
 * Crouch and prone, the sim half (decision 65, BUILDLIST 62.2; the posed sizes are lane E's STANCE_SIZE).
 *
 * A person stands (the default, absent on PlayerState), crouches or lies prone. The stance sets:
 * - **the eye and the muzzle**: lower by the posed eye heights (crouched 0.76 of standing, prone 0.34) — so
 *   cover hides more of a crouched person, and sight and shots start lower;
 * - **the hit volume**: standing keeps today's sphere exactly; crouched, that sphere scaled by the posed top
 *   (0.80); prone, three low spheres along the body, front to back as long as the posed body;
 * - **the pace**: a crouched walk is 0.55 of a walk, a crawl 0.25 (STANCE_SPEED, constants/action.ts); only a standing person jumps.
 * Everything else (cover, sight, the bots' aim) follows from the lower points. Pure.
 */

import type { PlayerState, Stance } from '../types';
import { STANCE_SIZE, REAL_TIMING } from '../constants';
import { PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';

/** How far a stance lowers the eye (and the muzzle): its posed eye height over standing's. */
export function stanceEyeScale(stance: Stance | undefined): number {
  return stance && stance !== 'stand' ? STANCE_SIZE[stance].eye / STANCE_SIZE.stand.eye : 1;
}

/** One sphere of a person's hit volume: its centre `up` above the feet and `along` the heading, its radius `r` (world units). */
export interface HitSphere { up: number; along: number; r: number }

/**
 * A person's hit volume in a stance. Standing is today's sphere (ps.hitboxCenter, ps.hitboxRadius) exactly;
 * crouched, scaled by the posed top; prone, three spheres as tall as the posed body lying, spread along it.
 */
export function stanceSpheres(stance: Stance | undefined, ps: Pick<PersonSet, 'hitboxCenter' | 'hitboxRadius' | 'k'>): HitSphere[] {
  if (!stance || stance === 'stand') return [{ up: ps.hitboxCenter, along: 0, r: ps.hitboxRadius }];
  if (stance === 'crouch') {
    const s = STANCE_SIZE.crouch.top / STANCE_SIZE.stand.top;
    return [{ up: ps.hitboxCenter * s, along: 0, r: ps.hitboxRadius * s }];
  }
  const w = (PERSON_BODY_HEIGHT * ps.k) / 1.8;                       // the posed model is 1.8 tall
  const r = (STANCE_SIZE.prone.top / 2) * w;
  const off = (STANCE_SIZE.prone.length / 2 - STANCE_SIZE.prone.top / 2) * w;
  return [{ up: r, along: -off, r }, { up: r, along: 0, r }, { up: r, along: off, r }];
}

/** The height a person's body is aimed at (its middle sphere's centre). */
export function stanceCentreHeight(stance: Stance | undefined, ps: Pick<PersonSet, 'hitboxCenter' | 'hitboxRadius' | 'k'>): number {
  const s = stanceSpheres(stance, ps);
  return s[s.length >> 1].up;
}

/**
 * 216 (GameConfig.realTiming): the eye's scale now — the stance's, or, while a change runs (`stanceShift`), eased from where
 * it began to the new stance's (linear over the change). Without a change exactly `stanceEyeScale(p.stance)`.
 */
export function eyeScaleOf(p: Pick<PlayerState, 'stance' | 'stanceShift'>): number {
  const to = stanceEyeScale(p.stance), s = p.stanceShift;
  if (!s || s.total <= 0) return to;
  const t = Math.max(0, Math.min(1, 1 - s.left / s.total));
  return s.fromEye + (to - s.fromEye) * t;
}

/** 216: the stance the body's hit shape has now — the old one for the first half of a change (`stanceShift`), else the stance. */
export function bodyStanceOf(p: Pick<PlayerState, 'stance' | 'stanceShift'>): Stance | undefined {
  const s = p.stanceShift;
  if (s && s.left > s.total / 2) return s.from === 'stand' ? undefined : s.from;
  return p.stance;
}

/**
 * 216: how long a stance change takes (REAL_TIMING; the drawn body's times): a step's own, or standing to prone and back
 * through the kneel — the second step begun at `stanceVia` of the first, as the view draws it. 0 for no change.
 */
export function stanceChangeSeconds(from: Stance, to: Stance): number {
  const S = REAL_TIMING.stanceSeconds, via = REAL_TIMING.stanceVia;
  if (from === to) return 0;
  if (from === 'stand' && to === 'prone') return S['stand>crouch'] * via + S['crouch>prone'];
  if (from === 'prone' && to === 'stand') return S['prone>crouch'] * via + S['crouch>stand'];
  return S[`${from}>${to}`] ?? 0;
}
